/**
 * UniqueAddEngineFields — presentational sub of `UniqueAddFormBody` covering
 * the per-engine extras (mssql clustered radios, postgres INCLUDE input,
 * postgres NULLS DISTINCT radios, oracle tablespace input, postgres+oracle
 * DEFERRABLE radios). Orchestrator owns state; this component renders the
 * input controls and propagates change events through setters.
 *
 * Split from `UniqueAddFormBody` per the 200-line file budget (Slice A4 +
 * Slice B2 split pattern).
 *
 * PRD: Ralph/VqeDdlEdit/PRD.md § Slice B3.
 */
import { Input } from '../../../../../components/ui/input';
import { Label } from '../../../../../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../../../../../components/ui/radio-group';

export type ClusteredMode = 'default' | 'clustered' | 'nonclustered';
export type DeferrableMode = 'unspecified' | 'deferred' | 'immediate';
export type NullsMode = 'unspecified' | 'distinct' | 'notDistinct';

export interface UniqueAddEngineFieldsProps {
  readonly testId: string;
  readonly showClustered: boolean;
  readonly showInclude: boolean;
  readonly showNullsDistinct: boolean;
  readonly showTablespace: boolean;
  readonly showDeferrable: boolean;
  readonly nullsDistinctNote: string | null;
  readonly clustered: ClusteredMode;
  readonly include: string;
  readonly nullsMode: NullsMode;
  readonly tablespace: string;
  readonly deferrable: DeferrableMode;
  readonly setClustered: (m: ClusteredMode) => void;
  readonly setInclude: (s: string) => void;
  readonly setNullsMode: (m: NullsMode) => void;
  readonly setTablespace: (s: string) => void;
  readonly setDeferrable: (m: DeferrableMode) => void;
}

export function UniqueAddEngineFields(p: UniqueAddEngineFieldsProps) {
  return (
    <>
      {p.showClustered ? (
        <fieldset className="flex flex-col gap-1">
          <legend className="text-xs">Clustering</legend>
          <RadioGroup
            name={`${p.testId}-clustered`}
            value={p.clustered}
            onValueChange={(v) => p.setClustered(v as ClusteredMode)}
            className="flex items-center gap-3 text-xs"
          >
            {(['default', 'clustered', 'nonclustered'] as ClusteredMode[]).map((mode) => (
              <label key={mode} className="flex items-center gap-1">
                <RadioGroupItem value={mode} data-testid={`${p.testId}-clustered-${mode}`} />
                <span className="capitalize">{mode === 'default' ? 'Engine default' : mode}</span>
              </label>
            ))}
          </RadioGroup>
        </fieldset>
      ) : null}

      {p.showInclude ? (
        <div className="flex flex-col gap-1">
          <Label htmlFor={`${p.testId}-include`} className="text-xs" title="Requires PostgreSQL 11+">INCLUDE columns</Label>
          <Input
            id={`${p.testId}-include`}
            data-testid={`${p.testId}-include`}
            value={p.include}
            onChange={(e) => p.setInclude(e.target.value)}
            placeholder="note, status"
            className="h-8 font-mono text-xs"
          />
        </div>
      ) : null}

      {p.showNullsDistinct ? (
        <fieldset className="flex flex-col gap-1">
          <legend className="text-xs" title="Requires PostgreSQL 15+">NULLS Distinct</legend>
          <RadioGroup
            name={`${p.testId}-nulls`}
            value={p.nullsMode}
            onValueChange={(v) => p.setNullsMode(v as NullsMode)}
            className="flex flex-wrap items-center gap-3 text-xs"
          >
            {(['unspecified', 'distinct', 'notDistinct'] as NullsMode[]).map((mode) => (
              <label key={mode} className="flex items-center gap-1">
                <RadioGroupItem value={mode} data-testid={`${p.testId}-nulls-${mode}`} />
                <span>
                  {mode === 'unspecified'
                    ? 'Engine default'
                    : mode === 'distinct' ? 'NULLS DISTINCT' : 'NULLS NOT DISTINCT'}
                </span>
              </label>
            ))}
          </RadioGroup>
          {p.nullsDistinctNote ? (
            <span className="text-[10px] text-muted-foreground" data-testid={`${p.testId}-nulls-version-note`}>
              {p.nullsDistinctNote}
            </span>
          ) : null}
        </fieldset>
      ) : null}

      {p.showTablespace ? (
        <div className="flex flex-col gap-1">
          <Label htmlFor={`${p.testId}-tablespace`} className="text-xs">USING INDEX TABLESPACE</Label>
          <Input
            id={`${p.testId}-tablespace`}
            data-testid={`${p.testId}-tablespace`}
            value={p.tablespace}
            onChange={(e) => p.setTablespace(e.target.value)}
            placeholder="TS_INDEX"
            className="h-8 font-mono text-xs"
          />
        </div>
      ) : null}

      {p.showDeferrable ? (
        <fieldset className="flex flex-col gap-1">
          <legend className="text-xs">Deferrable</legend>
          <RadioGroup
            name={`${p.testId}-deferrable`}
            value={p.deferrable}
            onValueChange={(v) => p.setDeferrable(v as DeferrableMode)}
            className="flex items-center gap-3 text-xs"
          >
            {(['unspecified', 'deferred', 'immediate'] as DeferrableMode[]).map((mode) => (
              <label key={mode} className="flex items-center gap-1">
                <RadioGroupItem value={mode} data-testid={`${p.testId}-deferrable-${mode}`} />
                <span className="capitalize">{mode === 'unspecified' ? 'Engine default' : mode}</span>
              </label>
            ))}
          </RadioGroup>
        </fieldset>
      ) : null}
    </>
  );
}
